// Collections — who owes what, when they were last contacted, and a statement by
// email. Registry page (features/registry.js, `feature: 'collections'`); App.js
// renders <FeatureOff/> when the feature is off, so this page never checks the gate
// to decide whether to exist.
//
// NOTHING ON THIS PAGE SENDS OR CHARGES ON ITS OWN. The list is a list: a biller
// works down it. A statement goes out only from the confirm in SendStatementModal,
// to the address on the patient's record, one patient per click. The "Due for
// contact" tab is the call list — cadence and follow-up dates put a patient on it;
// nothing is done to them automatically.
//
// Contract: docs/collections.md. Amounts are integer cents from the server.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { formatCurrency } from "../../utils/formatCurrency";
import { formatPatientName } from "../../utils/formatName";
import CardPanel from "./CardPanel";
import ExportButton from "../reports/ExportButton";
import ContactLog from "./ContactLog";
import SendStatementModal from "./SendStatementModal";
import {
  BUCKETS,
  PAYMENT_TYPES,
  centsToDollars,
  dollarsToCents,
  fetchSends,
  fetchSettings,
  fetchWorklist,
  formatDay,
  formatUtcDay,
  formatWhen,
  kindLabel,
  outcomeLabel,
  paymentTypeLabel,
  sendStatusChip,
  statementState,
} from "./api";
import "./Collections.css";

const MIN_BALANCE_DEBOUNCE_MS = 400;
const COLUMNS = 8;

function StatementSends({ practiceId, patientId, refreshKey, ambiguousErrors }) {
  const [sends, setSends] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setSends(null);
    setError(null);
    fetchSends(practiceId, patientId)
      .then((rows) => {
        if (!cancelled) setSends(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, patientId, refreshKey]);

  return (
    <div className="coll-sends">
      <h3>Statements emailed</h3>
      {error && (
        <div className="coll-message error" role="alert">
          {error}
        </div>
      )}
      {!error && sends === null && <p className="coll-muted">Loading…</p>}
      {sends && sends.length === 0 && (
        <p className="coll-muted">
          No statement has been emailed to this patient.
        </p>
      )}
      {sends && sends.length > 0 && (
        <table className="coll-mini-table">
          <thead>
            <tr>
              <th>Sent</th>
              <th>To</th>
              <th className="num">Amount</th>
              <th>Status</th>
              <th>By</th>
            </tr>
          </thead>
          <tbody>
            {sends.map((s) => {
              const chip = sendStatusChip(s.status, s.error, ambiguousErrors);
              return (
                <tr key={s.id}>
                  <td className="coll-nowrap">{formatWhen(s.sentAt)}</td>
                  <td>{s.toEmail || "—"}</td>
                  <td className="num">
                    {formatCurrency(centsToDollars(s.balanceCents))}
                  </td>
                  <td>
                    <span
                      className={`coll-chip ${chip.tone}`}
                      title={s.error || undefined}
                    >
                      {chip.label}
                    </span>
                  </td>
                  <td>{s.sentBy || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function CollectionsPage({
  selectedPractice,
  features = {},
  readOnly = false,
}) {
  const practiceId = selectedPractice?.id;

  const [bucket, setBucket] = useState("all");
  const [paymentType, setPaymentType] = useState("");
  const [minBalanceText, setMinBalanceText] = useState("");
  const [minBalanceCents, setMinBalanceCents] = useState(null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [sendFor, setSendFor] = useState(null); // patientId
  const [sendsKey, setSendsKey] = useState(0);
  // The server's own list of send errors that may still have delivered. Null until
  // it arrives (or if it cannot be read), when the local copy in api.js stands in.
  const [ambiguousErrors, setAmbiguousErrors] = useState(null);

  // Only the newest request may write, so a practice switch or a quick change of
  // tab cannot paint one list under another's filters.
  const ticket = useRef(0);
  // The practice on screen now. A write that finishes after a practice switch (a
  // send, a logged contact) calls the `load` and `onSent` of the render it started
  // in; they compare against this so the old practice's list never paints under
  // the new one.
  const practiceRef = useRef(practiceId);
  practiceRef.current = practiceId;

  const minBalanceInvalid = Number.isNaN(dollarsToCents(minBalanceText));

  useEffect(() => {
    const timer = setTimeout(() => {
      const cents = dollarsToCents(minBalanceText);
      setMinBalanceCents(Number.isNaN(cents) ? null : cents);
    }, MIN_BALANCE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [minBalanceText]);

  useEffect(() => {
    setData(null);
    setExpandedId(null);
    setSendFor(null);
    setMessage(null);
  }, [practiceId]);

  const load = useCallback(async () => {
    if (!practiceId || practiceId !== practiceRef.current) return;
    const mine = (ticket.current += 1);
    setLoading(true);
    setError(null);
    try {
      const body = await fetchWorklist(practiceId, {
        bucket,
        minBalanceCents,
        paymentType: paymentType || null,
      });
      if (mine !== ticket.current || practiceId !== practiceRef.current) return;
      setData(body);
    } catch (err) {
      if (mine !== ticket.current || practiceId !== practiceRef.current) return;
      setData(null);
      setError(err.message);
    } finally {
      if (mine === ticket.current) setLoading(false);
    }
  }, [practiceId, bucket, minBalanceCents, paymentType]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    setAmbiguousErrors(null);
    fetchSettings(practiceId)
      .then((body) => {
        if (!cancelled) setAmbiguousErrors(body.ambiguousSendErrors);
      })
      .catch(() => {
        // Not fatal: sendStatusChip falls back to the list compiled in.
        if (!cancelled) setAmbiguousErrors(null);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  if (!practiceId) {
    return (
      <div className="coll-page">
        <p className="coll-empty">Select a practice to see who owes what.</p>
      </div>
    );
  }

  const rows = data ? data.rows : [];
  const rules = data ? data.rules : null;
  const refundBucket = bucket === "refund_due";

  const onSent = (send, address) => {
    if (practiceId !== practiceRef.current) return;
    const patientId = sendFor;
    setSendFor(null);
    setMessage({
      type: "success",
      text: `Statement emailed to ${address}.`,
    });
    if (expandedId === patientId) setSendsKey((k) => k + 1);
    load();
  };

  return (
    <div className="coll-page">
      <div className="coll-header">
        <div>
          <h1>Collections</h1>
          <p className="coll-sub">
            Who owes {selectedPractice.name}, when they were last contacted, and
            a statement by email. Nothing here sends or charges on its own —
            every statement is one patient, your click, and a confirm.
          </p>
        </div>
        <button
          className="btn btn-secondary btn-sm"
          onClick={load}
          disabled={loading}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {rules && (
        <div className="coll-rules" data-testid="collections-rules">
          <span>
            Contact every <strong>{rules.cadenceDays ?? "—"}</strong> days
          </span>
          <span>
            No-show fees:{" "}
            <strong>{rules.chargeNoShowFee ? "on" : "off"}</strong>
          </span>
          <span>
            Charging overdue balances:{" "}
            <strong>{rules.chargeOverdue ? "on" : "off"}</strong>
          </span>
          {data.asOf && (
            <span className="coll-muted">as of {formatDay(data.asOf)}</span>
          )}
        </div>
      )}

      {message && (
        <div className={`coll-message ${message.type}`}>
          <span>{message.text}</span>
          <button
            className="coll-message-close"
            aria-label="Dismiss"
            onClick={() => setMessage(null)}
          >
            ×
          </button>
        </div>
      )}

      <div className="coll-filters">
        <div className="coll-tabs" role="tablist">
          {BUCKETS.map((b) => {
            const count = data ? data.counts[b.key] : null;
            return (
              <button
                key={b.key}
                role="tab"
                aria-selected={bucket === b.key}
                className={`coll-tab${bucket === b.key ? " active" : ""}`}
                onClick={() => {
                  setBucket(b.key);
                  setExpandedId(null);
                }}
              >
                {b.label}
                {count != null && (
                  <span className="coll-tab-count">{count}</span>
                )}
              </button>
            );
          })}
        </div>
        {/* Ruling O15. D owns the route and its logging; this is the button, on
            the page the list lives on. It exports the CALL LIST — who is due for
            contact, in calling order — not the tab or filters on screen, so it
            says so rather than reading "Export CSV" beside filters it ignores.
            Gated on `exports` as well as the page's own feature; ExportButton
            hides itself for the read-only client role, which the route refuses
            too. */}
        {features.exports && features.collections && (
          <ExportButton
            practiceId={practiceId}
            kind="collections-call-list"
            readOnly={readOnly}
            label="Export call list"
          />
        )}
        <label className="coll-inline-field">
          <span>Payment type</span>
          <select
            value={paymentType}
            onChange={(e) => setPaymentType(e.target.value)}
          >
            <option value="">Any</option>
            {PAYMENT_TYPES.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <label className="coll-inline-field">
          <span>Balance at least $</span>
          <input
            type="text"
            inputMode="decimal"
            className={minBalanceInvalid ? "is-invalid" : ""}
            value={minBalanceText}
            placeholder="0.00"
            onChange={(e) => setMinBalanceText(e.target.value)}
          />
        </label>
        {minBalanceInvalid && (
          <span className="coll-field-error">
            Enter an amount like 25 or 25.50
          </span>
        )}
      </div>

      {error && (
        <div className="coll-message error" role="alert">
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && data && rows.length === 0 && (
        <p className="coll-empty">
          {bucket === "due"
            ? "Nobody is due for contact."
            : refundBucket
              ? "No patient is owed a refund."
              : "No patient owes a balance."}
          {paymentType || minBalanceCents != null
            ? " (with these filters)"
            : ""}
        </p>
      )}

      {rows.length > 0 && (
        <div className="coll-table-wrap">
          <table className="coll-table">
            <thead>
              <tr>
                <th>Patient</th>
                <th className="num">
                  {refundBucket ? "Refund due" : "Balance"}
                </th>
                <th className="num">Visits</th>
                <th>Oldest visit</th>
                <th>Last statement</th>
                <th>Last contact</th>
                <th>Card on file</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const expanded = expandedId === row.patientId;
                const statement = row.lastStatement
                  ? sendStatusChip(
                      row.lastStatement.status,
                      row.lastStatement.error,
                      ambiguousErrors,
                    )
                  : null;
                // A patient nobody may charge because nobody has told them is
                // work, not an absence: these two states say so on the row and
                // carry "Send statement" as the action.
                const state = statementState(row);
                // `last_send_unconfirmed` is the server's own state now, so the
                // client no longer decides what an ambiguous failure means.
                const unconfirmed = row.statementState === "last_send_unconfirmed";
                const noSendWhy = !row.emailOnFile
                  ? "No email address on this patient's record"
                  : row.balanceCents <= 0
                    ? "Nothing owed, so there is no statement to send"
                    : null;
                return (
                  <React.Fragment key={row.patientId}>
                    <tr
                      className={
                        row.dueForContact ? "coll-row is-due" : "coll-row"
                      }
                    >
                      <td>
                        <button
                          className="coll-patient"
                          aria-expanded={expanded}
                          onClick={() =>
                            setExpandedId(expanded ? null : row.patientId)
                          }
                        >
                          {formatPatientName(row.patientName)}
                        </button>
                        <div className="coll-row-chips">
                          {row.selfPay && (
                            <span className="coll-chip warn">Cash pay</span>
                          )}
                          {!row.selfPay && row.paymentType !== "insurance" && (
                            <span className="coll-chip muted">
                              {paymentTypeLabel(row.paymentType)}
                            </span>
                          )}
                          {row.dueForContact && (
                            <span
                              className="coll-chip due"
                              title={row.dueReason || undefined}
                            >
                              Due · {row.dueReason || "contact"}
                            </span>
                          )}
                          {!row.emailOnFile && (
                            <span className="coll-chip muted">No email</span>
                          )}
                        </div>
                      </td>
                      <td className="num coll-amount">
                        {formatCurrency(
                          centsToDollars(
                            refundBucket
                              ? row.refundDueCents
                              : row.balanceCents,
                          ),
                        )}
                      </td>
                      <td className="num">{row.claimCount}</td>
                      <td className="coll-nowrap">
                        {row.oldestDos ? formatDay(row.oldestDos) : "—"}
                      </td>
                      <td>
                        {state && unconfirmed ? (
                          <>
                            <span
                              className={`coll-chip ${state.tone}`}
                              title={state.why}
                            >
                              {state.label}
                            </span>
                            {row.lastStatement && (
                              <div className="coll-muted">
                                tried {formatWhen(row.lastStatement.sentAt)}
                              </div>
                            )}
                            <div className="coll-muted">
                              May already have been sent — check before sending
                              another
                            </div>
                          </>
                        ) : state ? (
                          <>
                            <span
                              className={`coll-chip ${state.tone}`}
                              title={state.why}
                            >
                              {state.label}
                            </span>
                            {row.lastStatement && (
                              <div className="coll-muted">
                                tried {formatWhen(row.lastStatement.sentAt)}
                              </div>
                            )}
                            <div className="coll-muted">
                              Their overdue clock has not started
                            </div>
                          </>
                        ) : statement ? (
                          <>
                            <span className={`coll-chip ${statement.tone}`}>
                              {statement.label}
                            </span>
                            <div className="coll-muted">
                              {formatWhen(row.lastStatement.sentAt)}
                            </div>
                            {row.firstDeliveredSendAt && (
                              <div
                                className="coll-muted"
                                title="The overdue window is counted from here, not from the visit."
                              >
                                reached {formatUtcDay(row.firstDeliveredSendAt)}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="coll-muted">Never</span>
                        )}
                      </td>
                      <td>
                        {row.lastContact ? (
                          <>
                            <div>
                              {kindLabel(row.lastContact.kind)}
                              {row.lastContact.outcome
                                ? ` · ${outcomeLabel(row.lastContact.outcome)}`
                                : ""}
                            </div>
                            <div className="coll-muted">
                              {formatWhen(row.lastContact.at)}
                            </div>
                          </>
                        ) : (
                          <span className="coll-muted">Never</span>
                        )}
                        {row.nextContactOn && (
                          <div className="coll-muted">
                            Follow up {formatDay(row.nextContactOn)}
                          </div>
                        )}
                      </td>
                      <td>
                        {row.card ? (
                          <span className="coll-nowrap">
                            {(row.card.brand || "Card").toUpperCase()} ····{" "}
                            {row.card.last4 || "—"}
                          </span>
                        ) : (
                          <span className="coll-muted">None</span>
                        )}
                      </td>
                      <td className="coll-actions">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() =>
                            setExpandedId(expanded ? null : row.patientId)
                          }
                        >
                          {expanded ? "Close" : "Contact log"}
                        </button>
                        {!readOnly && (
                          <button
                            className="btn btn-primary btn-sm"
                            disabled={!!noSendWhy}
                            title={
                              noSendWhy ||
                              (unconfirmed
                                ? "A statement may already have gone out. Check the statements emailed for this patient before sending another."
                                : row.nextAction === "send_statement" && state
                                  ? `${state.why} Sending one is the next action for this patient.`
                                  : "Review and confirm before anything is sent")
                            }
                            onClick={() => setSendFor(row.patientId)}
                          >
                            Send statement…
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="coll-detail-row">
                        <td colSpan={COLUMNS}>
                          <div className="coll-detail">
                            <ContactLog
                              key={row.patientId}
                              practiceId={practiceId}
                              patientId={row.patientId}
                              readOnly={readOnly}
                              today={data.asOf}
                              onLogged={load}
                            />
                            <StatementSends
                              practiceId={practiceId}
                              patientId={row.patientId}
                              refreshKey={sendsKey}
                              ambiguousErrors={ambiguousErrors}
                            />
                            <CardPanel
                              key={`card-${row.patientId}`}
                              practiceId={practiceId}
                              patientId={row.patientId}
                              patientName={row.patientName}
                              readOnly={readOnly}
                              onChanged={load}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {sendFor != null && (
        <SendStatementModal
          key={sendFor}
          practiceId={practiceId}
          patientId={sendFor}
          onClose={(attempted) => {
            setSendFor(null);
            if (attempted) {
              // A failed or ambiguous send still wrote a row; show it.
              setSendsKey((k) => k + 1);
              load();
            }
          }}
          onSent={onSent}
        />
      )}
    </div>
  );
}
